/**
 * @fileoverview UsuarioRepository - Repository for users collection
 */

import { DataRepository } from './base/DataRepository';
import type { Usuario, UsuarioExpanded } from '../types/collections/Usuario';
import { RepositoryError } from './base/DataRepository';

/**
 * Repository for managing Usuario records
 * Extends DataRepository with Usuario-specific methods
 */
export class UsuarioRepository extends DataRepository<Usuario> {
  constructor() {
    super('users');
  }

  /**
   * Get all operarios, supervisors, and administrators (users with operario, supervisor, or administrador role), including inactive ones
   * @returns Array of operarios/supervisors/administrators with id, name, email, and activo status
   */
  async getOperariosParaAutocompletado(): Promise<{ id: string; name?: string; email: string; activo?: boolean }[]> {
    try {
      // Primero obtener los IDs de los roles de Operario, Supervisor y Administrador (con mayúscula inicial)
      const roles = await this.pb.collection('rol').getFullList({
        filter: '(nombre = "Operario" || nombre = "Supervisor" || nombre = "Administrador")',
        fields: 'id,nombre'
      });

      const rolIds = roles.map(r => r.id);
      
      if (rolIds.length === 0) {
        console.warn('No se encontraron roles de Operario, Supervisor o Administrador');
        return [];
      }

      // Obtener todos los usuarios (activos e inactivos) que tengan uno de estos roles
      const usuarios = await this.pb.collection(this.collectionName).getFullList<Usuario>({
        filter: `(${rolIds.map(id => `rol = "${id}"`).join(' || ')})`,
        sort: 'name',
        fields: 'id,name,email,rol,activo'
      });

      console.log('Usuarios con rol Operario/Supervisor/Administrador obtenidos:', usuarios.length);
      console.log('Usuarios con datos:', usuarios.map(u => ({ 
        id: u.id, 
        name: u.name, 
        email: u.email, 
        rolId: u.rol,
        activo: u.activo
      })));

      return usuarios.map(usuario => ({
        id: usuario.id,
        name: usuario.name,
        email: usuario.email,
        activo: usuario.activo !== false // Default to true if undefined
      }));
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener operarios para autocompletado: ${error instanceof Error ? error.message : String(error)}`,
        'GET_OPERARIOS_AUTOCOMPLETE_ERROR'
      );
    }
  }

  /**
   * Get all active users
   * @returns Array of active users
   */
  async getAllActive(): Promise<Usuario[]> {
    try {
      return await this.getAll({
        filter: 'activo = true',
        sort: 'name'
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener usuarios activos: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ALL_ACTIVE_ERROR'
      );
    }
  }
}

